import type { RemovableRef } from '@vueuse/core';

export type UseContentsForUserType = {
  count: ComputedRef<number>;
  all: ComputedRef<UserContent[]>;
  mainContent: ComputedRef<UserContent>;
  currentContent: ComputedRef<UserContent>;
  currentPath: ComputedRef<string>;
  orderedList: ComputedRef<UserContent[]>;
  lastSavedSlug: RemovableRef<string>;
  saveSlug: (slug) => void;
};

export const useContentsForUserType = (
  userType:
    | ContentUserType.Default
    | ContentUserType.Child = ContentUserType.Default
): UseContentsForUserType => {
  const route = useRoute();

  const lastSavedSlug = useNomadStorage(`content-${userType}`, '');

  const { all: allContents } = useContents();

  const all = computed<UserContent[]>(() => {
    return allContents.value.filter((item) => {
      if (userType === ContentUserType.Child) {
        return item.type === UserContentType.ChildrenContent;
      } else {
        return item.type !== UserContentType.ChildrenContent;
      }
    });
  });

  const count = computed<number>(() => all.value.length);

  const mainContent = computed<UserContent>(() => {
    return all.value.find(
      (content) => content.type === UserContentType.MainContent
    );
  });

  const currentContent = computed<UserContent>(() => {
    let currentSlug;
    if (
      route.params.section !== Sections.ChildForYou &&
      route.params.lb_or_eval
    ) {
      currentSlug = route.params.lb_or_eval;
      const index = all.value.findIndex((lb) => lb.slug === currentSlug);
      if (index !== -1) {
        if (
          userType === ContentUserType.Child &&
          route.params.section &&
          route.params.section !== Sections.Revisions
        ) {
          currentSlug = null;
        } else if (route.params.section !== all.value[index].baseSection) {
          currentSlug = null;
        }
      }
      if (index === -1) currentSlug = null;
    }

    if (!currentSlug && lastSavedSlug.value /* && route.path !== '/' */) {
      currentSlug = lastSavedSlug.value;
    }

    if (!currentSlug) {
      if (mainContent.value) return mainContent.value;
      if (all.value.length > 0) return all.value[0];
      return null;
    }

    const currentContent = all.value.find((content) => {
      if (content.slug !== currentSlug) return false;
      if (userType === ContentUserType.Child && !content.memberId) return false;
      if (userType === ContentUserType.Child && content.multipleMember)
        return content.isDefaultContentMember;
      return true;
    });

    return currentContent;
  });

  const orderedList = computed(() => {
    return all.value.sort((content) => {
      if (content.multipleMember) {
        return content.isDefaultContentMember ? -1 : 1;
      }
      return content.id === currentContent.value.id ? -1 : 1;
    });
  });

  const currentPath = computed(() => {
    if (currentContent.value && currentContent.value.path)
      return currentContent.value.path;
    return null;
  });

  const saveSlug = (slug) => {
    lastSavedSlug.value = slug;
  };

  watchEffect(() => {
    const find = all.value.find((item) => item.slug === lastSavedSlug.value);
    if (all.value.length === 0) return;

    if (!find) {
      saveSlug('');
    }
  });

  return {
    count,
    all,
    mainContent,
    currentContent,
    currentPath,
    saveSlug,
    lastSavedSlug,
    orderedList,
  };
};
